/**
 * PlanActivationScreen
 *
 * The single loading screen for the Shopify App Pricing return (`/welcome`).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS A SHARED COMPONENT AND NOT INLINE MARKUP
 * ─────────────────────────────────────────────────────────────────────────────
 * Approving a plan leaves the App Bridge iframe entirely (`open(url, "_top")` in
 * routes/billing.jsx), so the return to /welcome is a FULL COLD BOOT. Several
 * independent gates render before WelcomePage can mount — the router gate, the
 * useGadget auth gate, and the /welcome Suspense boundary. Each one used to
 * render an anonymous "Loading…" of a slightly different size and position, so a
 * merchant who had just paid watched two-to-three spinners hand off to each other
 * before being told anything.
 *
 * Every one of those gates now renders THIS component, so the screen the merchant
 * sees at first paint is the same screen they see until the sync settles.
 *
 * ⚠️ welcome.jsx must IMPORT this, never re-declare the markup. The seam-free
 * guarantee is that every gate on the /welcome path renders THIS component, so
 * the rendered output is identical at each handoff. Two copies drift, and a
 * one-pixel drift reintroduces the visible remount this component exists to
 * remove.
 *
 * Note the guarantee is about the four sites that render PlanActivationScreen —
 * App.jsx's router gate, its auth gate, the /welcome Suspense fallback, and
 * welcome.jsx's "syncing" state. It is NOT equality with RouteLoader, whose
 * bare <s-stack minHeight:60vh> is a different shell entirely; RouteLoader
 * simply never renders on this path.
 *
 * ⚠️ size="large-100", NOT size="large". Both are legal for s-spinner
 * (polaris-types: 'base' | 'large' | 'large-100'), but large-100 is one step
 * LARGER on the SizeKeyword scale. The shell loaders all use large-100 while
 * welcome.jsx used large, so the spinner visibly changed size at the handoff on
 * top of changing position. That single token is what must match RouteLoader.
 *
 * UI note: Polaris WEB COMPONENTS (s-*), not Polaris React — every sibling route
 * does the same. JSX attributes must be camelCase; kebab-case is silently dropped.
 */
import { useAdminLoading } from "../utils/adminLoading";

export default function PlanActivationScreen() {
  // Also drive Shopify's own header progress bar, outside our iframe — the same
  // affordance the admin and first-party apps (Flow) use. See utils/adminLoading.js.
  useAdminLoading();

  return (
    <s-page heading="Activating your plan">
      <s-section>
        <s-stack direction="block" gap="base" alignItems="center">
          <s-spinner size="large-100" accessibilityLabel="Activating your plan" />
          <s-text color="subdued">
            Confirming your subscription with Shopify. This usually takes a moment.
          </s-text>
        </s-stack>
      </s-section>
    </s-page>
  );
}
